import { useState, useEffect, useRef } from 'react'
import type { GccProgressEvent, Settings } from '@shared/types'
import { GCC_AGENT_CATALOG_SUMMARY } from '@shared/gcc/agent-catalog'
import { GCC_QUICK_WIN_OFF_SUMMARY } from '@shared/gcc/quick-win'
import { useApi, useGenerationContext } from '../index'
import { GCC_STEP_LABELS } from './gcc-meta'
import { GccErrorDetails } from './GccErrorDetails'
import type { GccWizardFormData } from './GccWizard'

const GCC_COLOR = '#059669'

type Phase = 'idle' | 'running' | 'pipeline' | 'done' | 'failed'

function resolveApiKey(settings: Settings): string {
  if (settings.aiProvider === 'claude') return settings.anthropicApiKey
  if (settings.aiProvider === 'azure-openai') return settings.azureApiKey
  return settings.openaiApiKey
}

function aiProviderLabel(provider: string | undefined): string {
  if (provider === 'claude') return 'Claude (Anthropic)'
  if (provider === 'openai') return 'OpenAI'
  if (provider === 'azure-openai') return 'Azure OpenAI'
  return 'Claude (Anthropic)'
}

function calcProgress(progress: GccProgressEvent[]): number {
  const total = 5
  const done = progress.filter(p => p.status === 'done').length
  const running = progress.some(p => p.status === 'running') ? 1 : 0
  return Math.min(95, Math.round(((done * 2 + running) / (total * 2)) * 100))
}

function currentStepLabel(progress: GccProgressEvent[]): string {
  const running = progress.find(p => p.status === 'running')
  if (running) return running.label
  const done = [...progress].filter(p => p.status === 'done').sort((a, b) => b.step - a.step)[0]
  if (done) return done.label
  return GCC_STEP_LABELS[0]
}

interface Props {
  projectId: string
  formData: GccWizardFormData
  onBack: () => void
  onComplete: () => void
  /** Called after a skeleton project is deleted because generation failed to start. */
  onProjectDiscarded?: () => void
}

export default function GccStep_Generate({ projectId, formData, onBack, onComplete, onProjectDiscarded }: Props) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [jobId, setJobId] = useState<string | null>(null)
  const [progress, setProgress] = useState<GccProgressEvent[]>([])
  const [error, setError] = useState<string | null>(null)
  const [settingsAiProvider, setSettingsAiProvider] = useState<string | undefined>(undefined)
  const jobIdRef = useRef<string | null>(null)
  const phaseRef = useRef<Phase>('idle')
  jobIdRef.current = jobId
  phaseRef.current = phase

  const api = useApi()
  const { trackJob } = useGenerationContext()

  useEffect(() => {
    api.getSettings().then(s => setSettingsAiProvider(s.aiProvider)).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; api is a stable context reference
  }, [])

  useEffect(() => {
    if (!jobId || (phase !== 'running' && phase !== 'pipeline')) return

    const interval = setInterval(async () => {
      if (!api.getGccJob) return
      const state = await api.getGccJob(jobIdRef.current ?? '').catch(() => null)
      if (!state) return

      setProgress(state.progress)

      if (state.status === 'succeeded') {
        setPhase('done')
        onComplete()
        return
      }
      if (state.status === 'failed') {
        setPhase('failed')
        setError(state.error ?? 'Generation failed.')
        return
      }
      if (phaseRef.current === 'running') {
        const step0Done = state.progress.some(p => p.step === 0 && p.status === 'done')
        if (step0Done) setPhase('pipeline')
      }
    }, 3000)

    return () => clearInterval(interval)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api is a stable context reference; onComplete is a prop that may be a new function reference each parent render, so excluding it prevents the interval from restarting on every parent re-render
  }, [jobId, phase])

  const startGeneration = async () => {
    if (!api.generateGcc) {
      setPhase('failed')
      setError('GCC generation is not available on this platform.')
      return
    }
    setPhase('running')
    setError(null)
    let skeletonAttempted = false
    try {
      const settings = await api.getSettings()
      const apiKey = resolveApiKey(settings)
      const now = new Date().toISOString()

      // Set before the call: the save can land even if the client then sees an error.
      skeletonAttempted = true
      await api.saveProject({
        id: projectId,
        name: `${formData.clientName} GCC Design`,
        client: formData.clientName,
        industry: formData.industry,
        mode: 'gcc',
        createdAt: now,
        updatedAt: now,
        branding: formData.branding,
      } as Parameters<typeof api.saveProject>[0])

      const { jobId: jid, error: err } = await api.generateGcc({
        projectId,
        clientName: formData.clientName,
        industry: formData.industry,
        functionScope: formData.functionScope,
        eligibilityRules: formData.eligibilityRules,
        payRateTable: formData.payRateTable.length > 0 ? formData.payRateTable : undefined,
        automateFirstThresholdPct: formData.automateFirstThresholdPct,
        quickWinHorizonMonths: formData.quickWinHorizonMonths,
        provider: settings.aiProvider,
        apiKey,
        branding: formData.branding,
        uploadedFiles: formData.uploadedFiles.length > 0 ? formData.uploadedFiles : undefined,
      })
      if (err) throw new Error(err)

      setJobId(jid)
      trackJob(jid, projectId, 'gcc', formData.clientName, formData.branding.primaryColor)
    } catch (e) {
      // No job is tracked, so GenerationContext will never clean up the skeleton.
      // Delete it here so it doesn't linger as a "Processing..." card. Finish the
      // delete before showing Try Again, so a quick retry's re-save can't be lost
      // to this late delete. If a job did start (e.g. the 202 was lost), the server
      // discards its result once it finds the project gone.
      if (skeletonAttempted) {
        const deleted = await api.deleteProject(projectId).then(() => true, () => false)
        if (deleted) onProjectDiscarded?.()
      }
      setPhase('failed')
      setError(e instanceof Error ? e.message : 'Failed to start generation.')
    }
  }

  const pct = calcProgress(progress)
  const stepLabel = currentStepLabel(progress)
  const isRunning = phase === 'running' || phase === 'pipeline'
  const functionsDisplay = formData.functionScope.length === 0
    ? 'Full Enterprise'
    : formData.functionScope.join(', ')

  return (
    <div className="max-w-2xl mx-auto px-8 py-8">
      {/* Idle — summary card */}
      {phase === 'idle' && (
        <>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Generate TN Global Design</h2>
            <p className="text-sm text-gray-500 mt-1">
              Talent Navigator will run the 4-step design engine: task register, disposition tagging,
              5-tier sourcing model, and future workforce value case.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50 mb-8">
            {[
              { label: 'Client', value: formData.clientName },
              { label: 'Industry', value: formData.industry || '—' },
              { label: 'Functions', value: functionsDisplay },
              {
                label: 'Eligibility rules',
                value: null,
                custom: (
                  <div className="text-sm text-gray-700 flex flex-wrap gap-x-4 gap-y-1">
                    {formData.eligibilityRules.map((r, i) => {
                      const vals = Array.isArray(r.value) ? r.value.join(', ') : r.value
                      const prefix = r.operator === 'in' ? 'includes' : r.operator === 'not-in' || r.operator === 'not-equals' ? 'excludes' : r.operator
                      return (
                        <span key={i} className="text-xs">
                          <span className="text-gray-400">{r.field}</span>{' '}
                          <span className="text-emerald-600 font-medium">{prefix}</span>{' '}
                          <span className="font-medium text-gray-800">{vals}</span>
                        </span>
                      )
                    })}
                  </div>
                ),
              },
              {
                label: 'Automate first',
                // WI 2886: 100% applies to no task
                value: formData.automateFirstThresholdPct >= 100
                  ? 'Off (rule not applied)'
                  : `Tasks AI releases ${formData.automateFirstThresholdPct}% or more of stay Retained`,
              },
              {
                label: 'Quick wins',
                value: formData.quickWinHorizonMonths === 0
                  ? GCC_QUICK_WIN_OFF_SUMMARY
                  : `Low-complexity Automate / Agentify tasks live within ${formData.quickWinHorizonMonths} months are agentified first`,
              },
              { label: 'Agent catalog', value: GCC_AGENT_CATALOG_SUMMARY },
              formData.uploadedFiles.length > 0 ? {
                label: 'Taxonomy source',
                value: null,
                custom: (
                  <div className="flex flex-col gap-0.5">
                    {formData.uploadedFiles.map((f, i) => (
                      <span key={i} className="text-sm text-gray-700 flex items-center gap-1.5">
                        <span>📎</span> <span className="font-medium">{f.name}</span>
                      </span>
                    ))}
                  </div>
                ),
              } : null,
              { label: 'AI Provider', value: aiProviderLabel(settingsAiProvider) },
            ].filter((row): row is NonNullable<typeof row> => Boolean(row)).map((r) => (
              <div key={r.label} className="flex items-start gap-4 px-6 py-3">
                <span className="text-xs text-gray-400 w-36 flex-shrink-0 pt-0.5">{r.label}</span>
                {r.custom ?? <span className="text-sm font-semibold text-gray-800">{r.value}</span>}
              </div>
            ))}
          </div>

          <div className="flex justify-between">
            <button
              onClick={onBack}
              className="px-6 py-2.5 rounded-lg text-gray-600 font-semibold text-sm border border-gray-200 hover:bg-gray-50 transition-all"
            >
              ← Back
            </button>
            <button
              onClick={() => void startGeneration()}
              className="flex items-center gap-2 px-8 py-2.5 rounded-lg text-white font-semibold text-sm hover:opacity-90 transition-all"
              style={{ background: GCC_COLOR }}
            >
              🏗️ Generate TN Global Design
            </button>
          </div>
        </>
      )}

      {/* Running / pipeline */}
      {isRunning && (
        <>
          <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">
              Generation Progress
            </p>

            {/* Progress bar */}
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>{stepLabel}</span>
              <span className="font-semibold" style={{ color: GCC_COLOR }}>{pct}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
              <div
                className="h-2 rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, background: GCC_COLOR }}
              />
            </div>

            {/* Current step */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4 animate-spin flex-shrink-0" style={{ color: GCC_COLOR }} fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              {stepLabel}
            </div>

            {/* Navigate away notice */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-50">
              <p className="text-xs text-gray-400 flex items-center gap-1">
                <span>💡</span>
                You can safely navigate away — generation continues in the background.
              </p>
              <button
                onClick={() => {
                  if (!jobIdRef.current) return
                  if (window.confirm('Cancel this generation? Progress will be lost.')) {
                    if (api.cancelGccJob) void api.cancelGccJob(jobIdRef.current)
                    setPhase('failed')
                    setError('Generation cancelled.')
                  }
                }}
                className="text-xs text-red-500 hover:text-red-700 font-medium flex-shrink-0 ml-4"
              >
                Cancel
              </button>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              disabled
              className="px-6 py-2.5 rounded-lg text-gray-400 font-semibold text-sm border border-gray-100 cursor-not-allowed"
            >
              ← Back
            </button>
            <button
              disabled
              className="flex items-center gap-2 px-8 py-2.5 rounded-lg text-white font-semibold text-sm opacity-60 cursor-not-allowed"
              style={{ background: GCC_COLOR }}
            >
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Generating TN Global Design...
            </button>
          </div>
        </>
      )}

      {/* Failed */}
      {phase === 'failed' && (
        <div className="text-center space-y-4">
          <div className="text-5xl">❌</div>
          <h2 className="text-xl font-semibold text-gray-900">Generation failed</h2>
          <GccErrorDetails message={error ?? ''} />
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={onBack}
              className="px-6 py-2.5 rounded-lg text-gray-600 font-semibold text-sm border border-gray-200 hover:bg-gray-50"
            >
              ← Back
            </button>
            <button
              onClick={() => { setPhase('idle'); setJobId(null); setProgress([]) }}
              className="px-6 py-2.5 rounded-lg text-white font-semibold text-sm hover:opacity-90"
              style={{ background: GCC_COLOR }}
            >
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
